<template>
  <div class="water-type-analysis">
    <div v-if="loading" class="loading-container">
      <div class="loading-spinner"></div>
      <p>{{ $t('加载中...') }}</p>
    </div>
    
    <div v-else class="chart-container">
      <div class="chart-wrapper">
        <Echart ref="chartRef" :options="chartOptions" style="height: 100%; width: 100%" />
      </div>
      
      <div class="stats-summary">
        <div class="total-users">
          <span class="total-number">{{ totalUsers }}</span>
          <span class="total-label">{{ $t('总用户数') }}</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { putAction } from '@/api/manage'

export default {
  name: 'WaterTypeAnalysis',
  data() {
    return {
      loading: true,
      totalUsers: 0,
      waterTypeData: [],
      chartOptions: {},
      intervalId: null,
      angle: 0,
      url: {
        doQueryMeterIndexTotalUser: "/jeecg-charge/meter/waterAnalysis/doQueryMeterIndexTotalUser"
      }
    }
  },
  mounted() {
    this.initChart()
  },
  beforeDestroy() {
    if (this.intervalId) {
      clearInterval(this.intervalId)
      this.intervalId = null
    }
  },
  methods: {
    async initChart() {
      try {
        this.loading = true
        await this.fetchData()
        // this.setupChart()
        this.setOption()
        this.startAnimation()
      } catch (error) {
        console.error('Failed to initialize chart:', error)
      } finally {
        this.loading = false
      }
    },
    
    async fetchData() {
      try {
        const response = await putAction(this.url.doQueryMeterIndexTotalUser, {})
        if (response.success) {
          const data = response.result
          this.totalUsers = data.meterUserTotal || 0
          this.waterTypeData = data.indexUserNumKindMessageList || []
        }
      } catch (error) {
        console.error('Failed to fetch water type data:', error)
        // 使用模拟数据
        this.waterTypeData = [
          { meterUserPriceName: '居民用水', meterUserNum: 8560 },
          { meterUserPriceName: '商业用水', meterUserNum: 2340 },
          { meterUserPriceName: '工业用水', meterUserNum: 1560 },
          { meterUserPriceName: '行政用水', meterUserNum: 896 }
        ]
        this.totalUsers = this.waterTypeData.reduce((sum, item) => sum + item.meterUserNum, 0)
      }
    },
    
    setupChart() {
      const seriesData = this.waterTypeData.map(item => ({
        name: item.meterUserPriceName,
        value: item.meterUserNum
      }))

      this.chartOptions = {
        color: [
          '#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4', '#FFEAA7',
          '#DDA0DD', '#98D8C8', '#F7DC6F', '#BB8FCE', '#85C1E9'
        ],
        tooltip: {
          trigger: 'item',
          backgroundColor: 'rgba(0, 11, 42, 0.9)',
          borderColor: '#5D6A94',
          borderWidth: 1,
          borderRadius: 8,
          textStyle: {
            color: '#CEE2FF'
          },
          formatter: (params) => {
            const percent = ((params.value / this.totalUsers) * 100).toFixed(1)
            return `${params.name}<br/>${this.$t('数量')}: ${params.value}${this.$t('户')}<br/>${this.$t('占比')}: ${percent}%`
          }
        },
        title: {
          text: `{total|${this.totalUsers}}{unit|${this.$t('户')}}`,
          textStyle: {
            color: '#52C4DF',
            rich: {
              total: {
                color: '#52C4DF',
                fontWeight: 500,
                fontSize: 24,
                fontFamily: 'ds-digital-bold'
              },
              unit: {
                color: '#2A6BAC',
                fontSize: 12
              }
            }
          },
          left: 'center',
          top: 'center',
          itemGap: 0,
          subtext: this.$t('用户总数'),
          subtextStyle: {
            color: '#fff',
            verticalAlign: 'middle',
            lineHeight: 12
          }
        },
        series: [
          {
            name: 'ring5',
            type: 'custom',
            coordinateSystem: 'none',
            renderItem: (params, api) => ({
              type: 'arc',
              shape: {
                cx: api.getWidth() / 2,
                cy: api.getHeight() / 2,
                r: Math.min(api.getWidth(), api.getHeight()) / 2 * 0.35,
                startAngle: (0 + this.angle) * Math.PI / 180,
                endAngle: (90 + this.angle) * Math.PI / 180
              },
              style: {
                stroke: '#00A2F3',
                opacity: 0.5,
                fill: 'transparent',
                lineWidth: 2
              },
              silent: true
            }),
            data: [0]
          },
          {
            name: 'ring5',
            type: 'custom',
            coordinateSystem: 'none',
            renderItem: (params, api) => ({
              type: 'arc',
              shape: {
                cx: api.getWidth() / 2,
                cy: api.getHeight() / 2,
                r: Math.min(api.getWidth(), api.getHeight()) / 2 * 0.35,
                startAngle: (120 + this.angle) * Math.PI / 180,
                endAngle: (210 + this.angle) * Math.PI / 180
              },
              style: {
                stroke: '#00A2F3',
                opacity: 0.5,
                fill: 'transparent',
                lineWidth: 2
              },
              silent: true
            }),
            data: [0]
          },
          {
            name: 'ring5',
            type: 'custom',
            coordinateSystem: 'none',
            renderItem: (params, api) => ({
              type: 'arc',
              shape: {
                cx: api.getWidth() / 2,
                cy: api.getHeight() / 2,
                r: Math.min(api.getWidth(), api.getHeight()) / 2 * 0.35,
                startAngle: (240 + this.angle) * Math.PI / 180,
                endAngle: (330 + this.angle) * Math.PI / 180
              },
              style: {
                stroke: '#00A2F3',
                fill: 'transparent',
                opacity: 0.5,
                lineWidth: 2
              },
              silent: true
            }),
            data: [0]
          },
          {
            name: this.$t('用水类型'),
            type: 'pie',
            radius: ['30%', '50%'],
            center: ['50%', '65%'],
            roseType: 'radius',
            animationDelay: 100,
            animationDuration: 2000,
            itemStyle: {
              borderRadius: 8,
              borderColor: '#fff',
              borderWidth: 1,
              shadowBlur: 8,
              shadowColor: 'rgba(0, 0, 0, 0.2)'
            },
            label: {
              show: true,
              position: 'outside',
              formatter: (params) => {
                const percent = ((params.value / this.totalUsers) * 100).toFixed(1)
                return `{name|${params.name}}\n{value|${params.value}${this.$t('户')}} {percent|(${percent}%)}`
              },
              rich: {
                name: {
                  color: '#fff',
                  fontSize: 9,
                  fontWeight: 'bold'
                },
                value: {
                  color: '#40a9ff',
                  fontSize: 8,
                  fontWeight: 'bold'
                },
                percent: {
                  color: 'rgba(255, 255, 255, 0.7)',
                  fontSize: 8
                }
              }
            },
            labelLine: {
              smooth: 0.2,
              length: 10,
              length2: 10,
              lineStyle: {
                color: 'rgba(255, 255, 255, 0.3)'
              }
            },
            emphasis: {
              itemStyle: {
                shadowBlur: 20,
                shadowColor: 'rgba(0, 0, 0, 0.4)'
              },
              label: {
                fontSize: 10
              }
            },
            minAngle: 5,
            data: seriesData
          }
        ]
      }
    },
    setOption() {
        let angle = this.angle
        let data = this.waterTypeData
        let total = this.totalUsers;
        let seriesData = []
        for (let i = 0; i < data.length; i++) {
          seriesData.push({
            name: data[i].meterUserPriceName,
            value: data[i].meterUserNum,
          })
        }
        // if(this.angle == 5){
        //   this.$refs.option.hideLoading()
        // }
        this.chartOptions = {
          color: ['#FECA20', '#50E3C2', '#FA6400', '#0091FF', '#0394FF', '#4634FA', '#0C2D82', '#B620E0','#32C5FF', '#99CC33',  '#E02020','#3A7BD5','#7ffa34','#CC9933','#FF99CC','#666633','#006600','#990033','#CCCCFF'],
          tooltip: {
            trigger: 'item',
            backgroundColor: 'rgba(0,11,42,0.9)',
            borderColor: '#5D6A94',
            borderWidth: 1,
            borderRadius: 8,
            textStyle: {
              color: '#CEE2FF',
            },
            axisPointer: {
              type: 'line',
              lineStyle: {
                color: '#019AF3'
              }
            }
          },
          grid: {
            top: '20%',
            left: '3%',
            right: '2%',
            containLabel: true
          },
          title: {
            text: `{total|${total}}{unit|${this.$t('户')}}`,
            textStyle: {
              color: '#52C4DF',
              rich: {
                total: {
                  color: '#52C4DF',
                  fontWeight: 500,
                  fontSize: 30,
                  fontFamily: 'ds-digital-bold'
      
                },
                unit: {
                  color: '#2A6BAC',
                  fontSize: 12,
                }
              }
            },
            left: 'center',
            top: '50%',
            itemGap: 0,
            subtext: this.$t('用户总数'),
            subtextStyle: {
              color: '#fff',
              verticalAlign: 'middle',
              lineHeight: 12
            }
          },
          series: [
            {
              name: "ring5",
              type: 'custom',
              coordinateSystem: "none",
              renderItem: function(params, api) {
                return {
                  type: 'arc',
                  shape: {
                    cx: api.getWidth() / 2,
                    cy: api.getHeight() / 2 + 22,
                    r: Math.min(api.getWidth(), api.getHeight()) / 2 * 0.35,
                    startAngle: (0 + angle) * Math.PI / 180,
                    endAngle: (90 + angle) * Math.PI / 180
                  },
                  style: {
                    stroke: "#00A2F3",
                    opacity: 0.5,
                    fill: "transparent",
                    lineWidth: 2
                  },
                  silent: true
                };
              },
              data: [0]
            },
            {
              name: "ring5",
              type: 'custom',
              coordinateSystem: "none",
              renderItem: function(params, api) {
                return {
                  type: 'arc',
                  shape: {
                    cx: api.getWidth() / 2,
                    cy: api.getHeight() / 2 + 22,
                    r: Math.min(api.getWidth(), api.getHeight()) / 2 * 0.35,
                    startAngle: (120 + angle) * Math.PI / 180,
                    endAngle: (210 + angle) * Math.PI / 180
                  },
                  style: {
                    stroke: "#00A2F3",
                    opacity: 0.5,
                    fill: "transparent",
                    lineWidth: 2
                  },
                  silent: true
                };
              },
              data: [0]
            },
            {
              name: "ring5",
              type: 'custom',
              coordinateSystem: "none",
              renderItem: function(params, api) {
                return {
                  type: 'arc',
                  shape: {
                    cx: api.getWidth() / 2,
                    cy: api.getHeight() / 2 + 22,
                    r: Math.min(api.getWidth(), api.getHeight()) / 2 * 0.35,
                    startAngle: (240 + angle) * Math.PI / 180,
                    endAngle: (330 + angle) * Math.PI / 180
                  },
                  style: {
                    stroke: "#00A2F3",
                    fill: "transparent",
                    opacity: 0.5,
                    lineWidth: 2
                  },
                  silent: true
                };
              },
              data: [0]
            },
            {
              name: '',
              type: 'pie',
              radius: ['45%', '55%'],
              center: ['50%', '60%'],
              animationDelay: 100,
              animationDuration: 2000,
              label: {
                show: true,
                fontSize: '12px',
                // minMargin: 50,
                // edgeDistance: 20,
                // lineHeight: 50,
                // alignTo: 'edge',
                // position: 'outer',
                // width: 0,
                // height: 30,
                // lineHeight: 0,
                // padding: [5, -2, 5, -2],
                // borderRadius: 2,
                // distanceToLabelLine: 0,
                // formatter: function(params) {
                //   let percent = 0
                //   if (total == 0) {
                //     percent = 100
                //   }
                //   let index = params.dataIndex
                //   percent = ((params.value / total) * 100).toFixed(0);
                //   return [
                //     `{topnumber${index}|${params.value}}{top${index}|个}{topnumber${index}|${percent}}{top${index}|%}\n{bottom${index}|${params.name}}`,
                //     `{hr${index}|}`
                //   ].join('\n')
                // },
                // align: 'center',
                // rich: {}
                // this.setRichText(color)
              },
              labelLine: {
                // lineStyle: {
                // 	type: [5]
                // },
                // normal: {
                //   smooth: 0.2,
                  // length: 2,
                  // length2: 1,
                // }
              },
              minAngle: 7,
              data: seriesData
            }
          ]
        };
        window.addEventListener('resize', function () {
          this.$refs.option.resize();
        })
      },
    
    startAnimation() {
      if (this.intervalId) {
        clearInterval(this.intervalId)
      }
      
      this.intervalId = setInterval(() => {
        this.angle = (this.angle + 5) % 360
        // this.setupChart()
        this.setOption()
      }, 100)
    },
    
    refresh() {
      this.initChart()
    }
  }
}
</script>

<style lang="less" scoped>
.water-type-analysis {
  width: 100%;
  height: 100%;
  position: relative;
  
  .loading-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    height: 100%;
    color: rgba(255, 255, 255, 0.6);
    
    .loading-spinner {
      width: 40px;
      height: 40px;
      border: 3px solid rgba(0, 150, 255, 0.3);
      border-top: 3px solid #0096ff;
      border-radius: 50%;
      animation: spin 1s linear infinite;
      margin-bottom: 1rem;
    }
    
    @keyframes spin {
      0% { transform: rotate(0deg); }
      100% { transform: rotate(360deg); }
    }
  }
  
  .chart-container {
    flex: 1;
    display: flex;
    flex-direction: column;
    min-height: 0;
    height: 100%;
  }
  
  .chart-wrapper {
    flex: 1;
    min-height: 0;
    height: 100%;
  }
  
  .stats-summary {
    position: absolute;
    top: 10px;
    right: 10px;
    
    .total-users {
      display: flex;
      flex-direction: column;
      align-items: center;
      background: rgba(0, 20, 40, 0.7);
      border: 1px solid rgba(0, 150, 255, 0.3);
      border-radius: 6px;
      padding: 0.5rem 1rem;
      backdrop-filter: blur(4px);
      
      .total-number {
        font-family: 'ds-digital-bold', monospace;
        font-size: 1.5rem;
        font-weight: bold;
        color: #40a9ff;
        text-shadow: 0 0 10px rgba(0, 150, 255, 0.5);
      }
      
      .total-label {
        font-size: 0.75rem;
        color: rgba(255, 255, 255, 0.7);
        margin-top: 0.25rem;
      }
    }
  }
}

// 响应式设计
@media (max-width: 768px) {
  .water-type-analysis {
    .stats-summary {
      position: static;
      margin-top: 1rem;
      
      .total-users {
        flex-direction: row;
        justify-content: center;
        gap: 0.5rem;
        
        .total-number {
          font-size: 1.25rem;
        }
        
        .total-label {
          margin-top: 0;
        }
      }
    }
  }
}

@media (max-width: 480px) {
  .water-type-analysis {
    .stats-summary {
      .total-users {
        padding: 0.25rem 0.5rem;
        
        .total-number {
          font-size: 1rem;
        }
        
        .total-label {
          font-size: 0.625rem;
        }
      }
    }
  }
}
</style>